import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
} from 'anf-core-react';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router';
import ExchangeSummary from '../exchange-summary/exchange-summary';
import SUBMIT_RETURN from '../../gql/order-return.gql';
import mapProductReturnDetail from '../../utils/map-product-return-detail';
import TmntText from '../tmnt-text/tmnt-text';
import CancelModal from '../cancel-modal/cancel-modal';
import trackAction from '../../utils/trackAction';
import getSignifydSessionID from '../../utils/signifyd-session-id';
import { useGetDigitalDataFlagValue } from '../../utils/digital-data-flag';
import trackQMAction from '../../utils/trackQMAction';
import parseJSON from '../../utils/parse-json';
import $window from '../../utils/window';

const valueTierMapping = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  'ultra high': 'high',
  churn: 'low',
  '': 'low',
};

export function getValueTier({
  $window,
  valueTierTealiumKey,
  valueTierSwitch,
  email,
  orderId,
}) {
  let valueTier = undefined;
  try {
    if (valueTierSwitch) {
      const tealiumData = $window.localStorage.getItem('tealium_va');
      const { key, havingSubBrand, subBrandKey } = valueTierTealiumKey;
      const parsedTealiumData = parseJSON(tealiumData, {});
      if (parsedTealiumData.properties
        && (parsedTealiumData.properties[key] || parsedTealiumData.properties[subBrandKey])
      ) {
        valueTier = parsedTealiumData.properties[key];
        if (havingSubBrand && !valueTier && parsedTealiumData.properties[subBrandKey]) {
          valueTier = parsedTealiumData.properties[subBrandKey];
        }
      }
      if (typeof valueTier !== 'string') {
        valueTier = valueTierMapping[''];
      }
      valueTier = valueTier.toLowerCase();
      if (valueTierMapping[valueTier]) {
        valueTier = valueTierMapping[valueTier];
      } else {
        trackQMAction(400, {
          visitorId: $window?.document?.cookie?.match(/rxVisitor=([^;]*)/i)?.[1],
          email,
          orderId,
          error: new Error('New valueTier not in mapping'),
          valueTier,
        });
        valueTier = valueTierMapping[''];
      }
    }
  } catch (error) {
     trackQMAction(400, {
      visitorId: $window?.document?.cookie?.match(/rxVisitor=([^;]*)/i)?.[1],
      email,
      orderId,
      error: new Error('Error getting valueTier'),
      errorMessage: error.message,
      valueTier,
    });
    valueTier = valueTierMapping[''];
  } finally {
    return valueTier;
  }
}

export function usingEGiftCard({
  selectedRefundMethod,
  exchangeOnly,
}) {
  if (exchangeOnly) {
    return false;
  }
  if (typeof selectedRefundMethod?.eGiftCard !== 'boolean') {
    return true;
  }
  return selectedRefundMethod.eGiftCard;
}

export function getItems({
  selectedProducts,
  selectedProductComments,
}) {
  return selectedProducts.map((product, idx) => {
    const comments = selectedProductComments[product.orderLineKey];
    return mapProductReturnDetail({ product, comments, seq: idx });
  })
}

export function usingSmartLabel({
  selectedShippingMethod,
}) {
  if (typeof selectedShippingMethod?.smartLabel !== 'boolean') {
    return true;
  }
  return selectedShippingMethod.smartLabel;
}

export function usingMerchCredit({
  selectedRefundMethod,
}) {
  if (typeof selectedRefundMethod?.merchCredit !== 'boolean') {
    return false;
  }
  return selectedRefundMethod.merchCredit;
}

export function getSubmitReturnVariables({
  selectedProducts,
  selectedProductComments,
  selectedShippingMethod,
  selectedRefundMethod,
  orderDetails,
  exchangeOnly,
  waiveReturnFee,
  valueTier,
  signifydSessionId,
  orderToken,
}) {
  const eGiftCard = usingEGiftCard({
    selectedRefundMethod,
    exchangeOnly,
  });

  const items = getItems({
    selectedProducts,
    selectedProductComments,
  });

  const smartLabel = usingSmartLabel({
    selectedShippingMethod,
  });

  const merchCredit = usingMerchCredit({
    selectedRefundMethod,
  });

  return {
    returnDetails: {
      billToKey: orderDetails.billToKey,
      currency: orderDetails.currency,
      // When we are performing an exchange only, set to false
      // NOTE: if the order is an exchange or the egc
      eGiftCard,
      fromOrderToken: orderToken,
      items,
      shipToKey: orderDetails.shipToKey,
      // NOTE: we should look at the shipping method to see if smartLabel is true or false
      smartLabel,
      // NOTE: signifydSessionId must be ported
      signifydSessionId,
      // NOTE: Search for Design+for+Fedex+QR+Code+User+Services+Changes in confluence.
      returnMethodPreference: selectedShippingMethod.returnMethodPreference,
      waiveReturnFee,
      valueTier,
      merchCredit,
    },
  };
}

export function trackSubmitReturn({
  selectedProducts,
  selectedShippingMethod,
  trackName,
  valueTier,
}) {
  const exchangeQuantity = selectedProducts.filter((p) => !!p.exchange).length;
  const returnQuantity = selectedProducts.filter((p) => !p.exchange).length;
  const pageDescription = returnQuantity > 0 ? 'return submit' : 'exchange submit';
  const trackData = {
    page_type: 'my account',
    page_description: pageDescription,
    exchange_quantity: exchangeQuantity,
    return_quantity: returnQuantity,
    return_method: selectedShippingMethod?.name || null,
    return_refund_method: trackName,
    valueTier,
  };

  trackAction('return_exchange', trackData);
}

export function trackReturnMethodPreference({
  selectedShippingMethod,
  $window,
  orderDetails,
  orderId,
}) {
  if (!selectedShippingMethod.returnMethodPreference) {
    const cookie = $window?.document?.cookie;
    trackQMAction(400, {
      visitorId: cookie?.match(/rxVisitor=([^;]*)/i)?.[1],
      email: orderDetails.email,
      orderId,
    });
  }
}

export function trackMissingReturnReasonCode({
  updatedReturnReasonCode,
  variables,
  orderDetails,
  orderId,
  $window,
}) {
  // Adding logging for scenario when return method preference is not Label or QR
  if (!updatedReturnReasonCode) {
    const itemsMissingReturnReasonCode = variables?.returnDetails?.items?.filter(
      (item) => !item.returnReasonCode,
    );
    if (itemsMissingReturnReasonCode.length > 0) {
      trackQMAction(400, {
        visitorId: $window?.document?.cookie?.match(/rxVisitor=([^;]*)/i)?.[1],
        email: orderDetails.email,
        orderId,
        error: new Error('Missing returnReasonCode in items'),
      });
    }
  } 
}

export function trackReturnReasons({
  selectedProductComments,
}) {
  const returnReasonsValues = Object.keys(selectedProductComments).map((key) => {
    const comments = selectedProductComments[key];
    return [
      comments?.bucket,
      comments?.reason1?.value,
      comments?.reason2?.value,
      comments?.reason3?.value,
    ].filter(Boolean).join('|');
  }).filter(Boolean).join(';');

  const trackData = {
    tealium_event: 'return_exchange',
    event_name: 'return_exchange',
    page_description: 'return submit',
    page_type: 'my account',
    return_reason: returnReasonsValues,
  };
  trackAction('return_exchange', trackData);
}

export default function FinalizeExchange({
  calculation,
  selectedProducts,
  selectedProductComments,
  selectedShippingMethod,
  selectedRefundMethod,
  orderDetail,
  next,
  waiveReturnFee,
  trackName,
}) {
  const valueTierSwitch = useGetDigitalDataFlagValue('ful-dre-2-0-value-tier', false);
  const updatedReturnReasonCode = useGetDigitalDataFlagValue('ful-dre-2-0-updated-reason-codes', false);
  const { orderId } = useParams();

  const [submitReturn, {
    loading, data,
  }] = useLazyQuery(SUBMIT_RETURN);

  const [isCancel, setIsCancel] = useState(false);

  const exchangeOnly = useMemo(
    () => selectedProducts.filter((p) => !!p.exchange).length === selectedProducts.length,
    [selectedProducts],
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    const { originalOrder } = orderDetail;
    const { orderDetails, orderToken, valueTierTealiumKey } = originalOrder;

    const signifydSessionId = getSignifydSessionID(document);
    const valueTier = getValueTier({
      $window,
      valueTierTealiumKey,
      valueTierSwitch,
      email: orderDetails.email,
      orderId,
    });

    const variables = getSubmitReturnVariables({
      selectedProducts,
      selectedProductComments,
      selectedShippingMethod,
      selectedRefundMethod,
      orderDetails,
      exchangeOnly,
      waiveReturnFee,
      valueTier,
      signifydSessionId,
      orderToken,
    });
    
    trackSubmitReturn({
      selectedProducts,
      selectedShippingMethod,
      trackName,
      valueTier,
    });

    trackReturnMethodPreference({
      selectedShippingMethod,
      $window,
      orderDetails,
      orderId,
    });

    trackMissingReturnReasonCode({
      updatedReturnReasonCode,
      variables,
      orderDetails,
      orderId,
      $window,
    });

    trackReturnReasons({
      selectedProductComments,
    })
   
    submitReturn({ variables });
  }, [
    selectedProducts,
    selectedProductComments,
    valueTierSwitch,
    selectedRefundMethod,
    selectedShippingMethod,
    orderDetail,
    updatedReturnReasonCode,
    orderId,
  ]);

  // Return submitted, clear session storage and navigate to the final screen
  useEffect(() => {
    if (!data) return;
    next(data);
  }, [data, next]);

  return (
    <div className="finalize-exchange">
      <ExchangeSummary
        calculation={calculation}
        variant="white"
        totalOnly
        className="mobile-only"
      />
      <ExchangeSummary
        calculation={calculation}
        className="mobile-hidden"
      />
      <div className="finalize-exchange-controls">
        <Button
          variant="primary"
          onClick={handleSubmit}
          isProcessing={loading}
          isFullWidth
        >
          <TmntText k="GLB_SUBMIT" />
        </Button>

        <Button
          variant="tertiary-light"
          onClick={() => setIsCancel(true)}
          isFullWidth
          classList="mobile-hidden"
        >
          <TmntText k="GLB_CANCEL" />
        </Button>
      </div>
      <CancelModal
        isOpen={isCancel}
        setOpen={setIsCancel}
        handleSubmit={() => {
          // note can't use `useNavigate` here because we don't have a route
          // declared so we're reloading the page instead
          window.location.reload();
        }}
      />
    </div>
  );
}

FinalizeExchange.propTypes = {
  calculation: PropTypes.shape({
    orderCalculation: PropTypes.shape({
      itemsRefundFmt: PropTypes.string,
      salesTaxRefundFmt: PropTypes.string,
      returnItemCount: PropTypes.string,
      exchangeItemCount: PropTypes.string,
      exchangeAmountFmt: PropTypes.string,
      shippingChargesFmt: PropTypes.string,
      totalRefundFmt: PropTypes.string,
    }),
  }),
  selectedProducts: PropTypes.arrayOf(
    PropTypes.shape({}),
  ),
  selectedShippingMethod: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    mapUrl: PropTypes.string,
    type: PropTypes.string,
    returnMethodId: PropTypes.number,
    returnMethodPreference: PropTypes.string,
    smartLabel: PropTypes.bool,
  }).isRequired,
  selectedProductComments: PropTypes.shape({}),
  orderDetail: PropTypes.shape({
    originalOrder: PropTypes.shape({
      orderToken: PropTypes.string,
      orderDetails: PropTypes.shape({
        billToKey: PropTypes.string,
        currency: PropTypes.string,
        shipToKey: PropTypes.string,
        email: PropTypes.string,
      }),
      valueTierTealiumKey: PropTypes.shape({
        key: PropTypes.string,
        havingSubBrand: PropTypes.bool,
        subBrandKey: PropTypes.string,
      }),
    }),
  }),
  next: PropTypes.func.isRequired,
  selectedRefundMethod: PropTypes.shape({
    eGiftCard: PropTypes.bool,
    merchCredit: PropTypes.bool,
    name: PropTypes.string,
  }),
  waiveReturnFee: PropTypes.bool,
  trackName: PropTypes.string,
};
